import Cart, { CartStore } from "cart-store";

import { formatMoney } from "utils";

// Free gifts with purchase (Theme settings > Free gift): two gift products (priced at 0), each
// with its own minimum. Once the products in the cart are worth more than a minimum, that gift
// is added to the cart on its own; past the second minimum the second gift takes the place of
// the first, so a cart only ever holds one gift, the highest one it reached. When the cart drops
// back, the gift goes back down or out. This module watches the cart on every page to keep that
// rule, and renders <cart-free-gift>, which tells how much is left until the next gift.
//
// Config comes from #cart-free-gift-data, rendered in layout/theme.liquid.

const dataElement = document.getElementById("cart-free-gift-data");
const config = dataElement ? JSON.parse(dataElement.textContent) : null;

// Every picked gift product, offered or not: any of them in the cart is a gift line.
const giftVariantIds = config ? config.gifts.map((gift => gift.variantId)) : [];

// The gifts offered right now (they have a minimum in the cart currency), lowest minimum first.
const tiers = config ? config.gifts.filter((gift => gift.goal !== null)).sort(((a, b) => a.goal - b.goal)) : [];

// Gifts that couldn't be added (sold out, not sold in this market, priced above 0): skipped,
// the gift below takes their place, until the next page load.
const blockedVariantIds = new Set;

const isGiftLine = item => giftVariantIds.includes(item.variant_id);

const giftLines = cart => (cart.items || []).filter(isGiftLine);

// The products that count toward the minimums (Theme settings > Free gift > Products that count
// toward the minimum), null when all of them do. Known at first for the products in the cart at
// page load; the first time another product shows up, the whole collection is read from
// templates/collection.free-gift-ids.liquid, and until then the gift and the bar stay as they are.
const eligibleConfig = config && config.eligible ? config.eligible : null;

const eligibleAtLoad = eligibleConfig ? new Map(Object.entries(eligibleConfig.products).map((([id, counts]) => [ Number(id), counts ]))) : null;

let eligibleProductIds = null;

let eligibleLoading = null;

// Called once the collection is read, to look at the cart again.
const eligibleListeners = new Set;

// Bought with a selling plan (Abonament MABO and any other subscription): never counts.
const isSubscriptionLine = item => Boolean(item.selling_plan_allocation);

const needsEligibility = item => eligibleConfig !== null && !isGiftLine(item) && !item.gift_card && !isSubscriptionLine(item);

const isEligibilityKnown = item => !needsEligibility(item) || eligibleProductIds !== null || eligibleAtLoad.has(item.product_id);

const allEligibilityKnown = cart => (cart.items || []).every(isEligibilityKnown);

function loadEligibleProducts() {
    if (eligibleLoading || eligibleProductIds) return;
    eligibleLoading = fetch(eligibleConfig.url).then((response => {
        if (!response.ok) throw new Error(`Free gift: ${eligibleConfig.url} answered ${response.status}`);
        return response.json();
    })).then((ids => {
        eligibleProductIds = new Set(ids);
        eligibleListeners.forEach((listener => listener()));
    })).catch((error => {
        // Tried again on the next cart change.
        console.error(error);
    })).finally((() => {
        eligibleLoading = null;
    }));
}

// Whether a cart line adds up toward the minimums: not a gift, not a gift card, not a
// subscription, and in the collection when one is picked.
const countsTowardGift = item => {
    if (isGiftLine(item) || item.gift_card || isSubscriptionLine(item)) return false;
    if (eligibleConfig === null) return true;
    if (eligibleProductIds !== null) return eligibleProductIds.has(item.product_id);
    return eligibleAtLoad.get(item.product_id) === true;
};

// Products after discounts, without shipping: the cart total (already net of discounts, never
// includes shipping) minus the gifts themselves, any gift cards, subscriptions and the products
// that don't count.
const eligibleTotal = cart => (cart.items || []).reduce(((total, item) => countsTowardGift(item) ? total : total - item.final_line_price), cart.total_price || 0);

const isOffered = cart => tiers.length > 0 && cart.currency === config.currency;

// How many gifts the cart reaches: the highest one reached is tiers[count - 1].
const reachedCount = cart => isOffered(cart) ? tiers.filter((tier => eligibleTotal(cart) >= tier.goal)).length : 0;

// The gift that belongs in the cart: the highest one reached that can be added.
const targetTier = cart => tiers.slice(0, reachedCount(cart)).filter((tier => !blockedVariantIds.has(tier.variantId))).pop() || null;

const hasGift = (cart, tier) => giftLines(cart).some((line => line.variant_id === tier.variantId && line.final_line_price === 0));

const moneyWithoutTrailingZeros = cents => formatMoney(cents, window.theme.money_format).replace(/([.,])00(?!\d)/, "");

// Straight to the Ajax API rather than Cart.add(), which would treat the gift like a product
// the customer added (cart notification, drawer opening on the cart page). Doesn't touch the
// cart store: the caller syncs it once the cart is final.
async function addGift(variantId) {
    try {
        await CartStore.addVariantsToCart({
            id: variantId,
            quantity: 1
        });
        return true;
    } catch (error) {
        blockedVariantIds.add(variantId);
        console.error(error);
        return false;
    }
}

let enforcing = false;

let enforceAgain = false;

// One line of the target gift with quantity 1, no other gift lines. A gift line that somehow
// costs money (price changed in the admin) is always taken out, and that gift isn't added again.
async function enforceRule() {
    if (enforcing) {
        enforceAgain = true;
        return;
    }
    const {cart: cart, lineItemsBeingUpdated: lineItemsBeingUpdated} = Cart.getState();
    // Quantity changes still on their way; the cart changes again once they land.
    if (lineItemsBeingUpdated.length > 0) return;
    // A product we don't know counts or not: runs again once the collection is read.
    if (!allEligibilityKnown(cart)) {
        loadEligibleProducts();
        return;
    }
    const lines = giftLines(cart);
    lines.forEach((line => {
        if (line.final_line_price !== 0) blockedVariantIds.add(line.variant_id);
    }));
    const target = targetTier(cart);
    let kept = false;
    const updates = lines.reduce(((lineUpdates, line) => {
        const keep = !kept && target !== null && line.variant_id === target.variantId && line.final_line_price === 0;
        if (keep) kept = true;
        const quantity = keep ? 1 : 0;
        if (line.quantity !== quantity) lineUpdates[line.key] = quantity;
        return lineUpdates;
    }), {});
    const needsGift = target !== null && !kept;
    const hasUpdates = Object.keys(updates).length > 0;
    if (!needsGift && !hasUpdates) return;
    enforcing = true;
    try {
        // Add the new gift first and take the old one out after, so the cart store goes straight
        // from one gift to the other.
        const added = needsGift ? await addGift(target.variantId) : true;
        if (added && hasUpdates) {
            await Cart.update({
                updates: updates
            }, "freeGift");
        } else {
            // Gift added, or it couldn't be: either way sync the store, which runs this again
            // (the gift below takes the place of one that couldn't be added).
            await Cart.refresh();
        }
    } catch (error) {
        if (!(error.name && error.name === "AbortError")) console.error(error);
    }
    enforcing = false;
    if (enforceAgain) {
        enforceAgain = false;
        enforceRule();
    }
}

// Confetti for each gift reached, like the free shipping bar: it plays once per gift, and the
// highest gift celebrated is remembered in this browser until the cart is emptied.
const ANIMATION_STORAGE_KEY = "freeGiftAnimationShown";

const celebratedCount = () => {
    try {
        return Number(localStorage.getItem(ANIMATION_STORAGE_KEY)) || 0;
    } catch (error) {
        return 0;
    }
};

const setCelebratedCount = count => {
    try {
        if (count > 0) {
            localStorage.setItem(ANIMATION_STORAGE_KEY, String(count));
        } else {
            localStorage.removeItem(ANIMATION_STORAGE_KEY);
        }
    } catch (error) {}
};

class CartFreeGift extends HTMLElement {
    #boundOnModalEvent;
    #celebrationTimer=null;
    #goalAnimation=null;
    #parentModal;
    #playingAnimation=false;
    #reachedCount=0;
    #boundRenderCurrentCart;
    constructor() {
        super();
        this.#boundOnModalEvent = this.#onModalEvent.bind(this);
        this.#boundRenderCurrentCart = () => this.#render(Cart.getState().cart);
    }
    connectedCallback() {
        if (!config) return;
        this.states = Array.from(this.querySelectorAll("free-gift-state"));
        this.track = this.querySelector(".free-gift-bar__track");
        this.markers = Array.from(this.querySelectorAll(".free-gift-bar__marker"));
        this.goalAnimationElement = this.querySelector("free-gift-goal-animation");
        // Like the free shipping bar, the progress fills up each time the cart drawer opens
        // (.is-visible starts the transition).
        this.#parentModal = this.closest("modal-dialog, popup-dialog");
        if (this.#parentModal) {
            this.#parentModal.addEventListener("on:modal:opened", this.#boundOnModalEvent);
            this.#parentModal.addEventListener("on:modal:closed", this.#boundOnModalEvent);
        }
        if (!this.#parentModal || this.#parentModal.shown) {
            requestAnimationFrame((() => requestAnimationFrame((() => this.classList.add("is-visible")))));
        }
        // The confetti loads with the drawer, not with the page.
        if (this.goalAnimationElement) {
            if (this.#parentModal && !this.#parentModal.shown) {
                this.#parentModal.addEventListener("on:modal:opening", (() => this.#loadGoalAnimation()), {
                    once: true
                });
            } else {
                this.#loadGoalAnimation();
            }
        }
        this.#render(Cart.getState().cart);
        this.unsubscribe = Cart.subscribe((state => this.#render(state.cart)));
        eligibleListeners.add(this.#boundRenderCurrentCart);
    }
    disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        eligibleListeners.delete(this.#boundRenderCurrentCart);
        if (this.#parentModal) {
            this.#parentModal.removeEventListener("on:modal:opened", this.#boundOnModalEvent);
            this.#parentModal.removeEventListener("on:modal:closed", this.#boundOnModalEvent);
        }
        if (this.#celebrationTimer) clearTimeout(this.#celebrationTimer);
        this.#celebrationTimer = null;
        this.classList.remove("is-visible");
    }
    #onModalEvent(event) {
        const opened = event.type === "on:modal:opened";
        this.classList.toggle("is-visible", opened);
        if (opened) this.#celebrate(500);
    }
    async #loadGoalAnimation() {
        if (this.#goalAnimation) return;
        this.#goalAnimation = "loading";
        const {DotLottie: DotLottie} = await import("dotlottie");
        this.#goalAnimation = new DotLottie({
            canvas: this.goalAnimationElement.querySelector("canvas"),
            loop: false,
            autoplay: false,
            src: this.goalAnimationElement.getAttribute("data-url")
        });
    }
    // Plays the confetti after the given delay (the bar fills up first) if the bar is in view and
    // shows a gift not celebrated yet.
    #celebrate(delay) {
        if (!this.goalAnimationElement || this.#celebrationTimer) return;
        this.#celebrationTimer = setTimeout((() => {
            this.#celebrationTimer = null;
            this.#playGoalAnimation();
        }), delay);
    }
    #playGoalAnimation() {
        const count = this.#reachedCount;
        if (this.#playingAnimation || this.hidden || !this.classList.contains("is-visible")) return;
        if (count === 0 || count <= celebratedCount()) return;
        setCelebratedCount(count);
        this.#playingAnimation = true;
        if (!this.#goalAnimation) this.#loadGoalAnimation();
        let attempts = 0;
        const interval = setInterval((() => {
            attempts += 1;
            const animation = this.#goalAnimation;
            if (animation && animation !== "loading" && animation.isLoaded) {
                clearInterval(interval);
                const canvas = this.goalAnimationElement.querySelector("canvas");
                if (canvas.getAttribute("width") === "0") {
                    canvas.setAttribute("width", 300);
                    canvas.setAttribute("height", 300);
                }
                animation.play();
                this.#playingAnimation = false;
            } else if (attempts > 50) {
                clearInterval(interval);
                this.#playingAnimation = false;
            }
        }), 100);
    }
    #render(cart) {
        // Not known yet whether a product counts: keep the current message until it is.
        if (!allEligibilityKnown(cart)) return;
        const target = targetTier(cart);
        // The gift is on its way into the cart (or about to replace the smaller one): keep the
        // current message until it lands.
        if (target && !hasGift(cart, target)) return;
        const count = reachedCount(cart);
        // Nothing to say when the gift reached can't be added and a smaller one stands in for it.
        const shown = isOffered(cart) && (count === 0 || target === tiers[count - 1]);
        this.toggleAttribute("hidden", !shown);
        this.#reachedCount = shown ? count : 0;
        if (!shown) return;
        if (count > celebratedCount()) this.#celebrate(1000);
        const total = eligibleTotal(cart);
        const next = tiers[count];
        this.states.forEach((state => {
            const visible = Number(state.getAttribute("reached")) === count;
            state.toggleAttribute("hidden", !visible);
            const amountLeft = state.querySelector("money-value");
            if (visible && next && amountLeft) {
                amountLeft.innerHTML = moneyWithoutTrailingZeros(next.goal - total);
            }
        }));
        // The bar runs up to the highest minimum; the markers are the gifts on the way.
        const progress = Math.min(Math.max(total, 0) / tiers[tiers.length - 1].goal, 1);
        if (this.track) this.track.style.setProperty("--displacement", `${Math.round((1 - progress) * 100)}%`);
        this.markers.forEach((marker => {
            marker.classList.toggle("is-reached", total >= Number(marker.getAttribute("goal")));
        }));
    }
}

if (config) {
    customElements.define("cart-free-gift", CartFreeGift);
    eligibleListeners.add(enforceRule);
    Cart.subscribe(((state, prevState) => {
        if (state.cart !== prevState.cart || state.lineItemsBeingUpdated.length !== prevState.lineItemsBeingUpdated.length) {
            enforceRule();
        }
        if (!state.cart.item_count) setCelebratedCount(0);
    }));
    if (!Cart.getState().cart.item_count) setCelebratedCount(0);
    enforceRule();
}
